import { Button } from '@components/ui'
import { useQuery } from '@tanstack/react-query'
import { Col, Flex, Row, Spin } from 'antd'
import DOMPurify from 'dompurify'

import {
  Entities_WebFlow_TutorialsAdditionalLinks_LinkItems,
  Entities_WebFlow_TutorialsLearningTools_TutorialsItem
} from '@suntex.fim/api-client'
import { useFramework } from '@suntex.fim/framework'
import { ResourceList } from '@suntex.fim/ui'

import ExploreLink from './ExploreLink'
import GameExampleSubCategory from './GameExampleSubCategory'
import { openVideo } from './util'

function TeacherTutorials() {
  const { api } = useFramework()
  const { data: additionalLinks } = useQuery({
    queryKey: ['webflow', 'tutorialsAdditionalLinks'],
    queryFn: async () =>
      (await api.get_WebFlow_TutorialsAdditionalLinks({})).links
  })
  const { data: webFlowTutorials } = useQuery({
    queryKey: ['webflow', 'tutorialsLearningTools'],
    queryFn: async () => {
      const r = await api.get_WebFlow_TutorialsLearningTools({})
      return r.categories.sort(
        (a, b) => a.name?.localeCompare(b.name ?? '') ?? 0
      )
    }
  })
  const { data: webFlowTutorialsGameExamples } = useQuery({
    queryKey: ['webflow', 'tutorialsGameExamples'],
    queryFn: async () => {
      const r = await api.get_WebFlow_TutorialsGameExamples({})
      return r.gameExamples.sort(
        (a, b) =>
          a['section-order']?.localeCompare(b['section-order'] ?? '') ?? 0
      )
    }
  })
  const { data: webFlowTutorialsVpd } = useQuery({
    queryKey: ['webflow', 'tutorialsVpd'],
    queryFn: async () => {
      const r = await api.get_WebFlow_TutorialsVpd({})
      return r.items.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0)
    }
  })

  if (
    !additionalLinks ||
    !webFlowTutorials ||
    !webFlowTutorialsGameExamples ||
    !webFlowTutorialsVpd
  )
    return <Spin />

  return (
    <Flex vertical>
      {/* heading */}
      <div className="main-navigation">
        <h3 className="main-navigation__heading">
          {webFlowTutorialsVpd[0]?.name}
        </h3>
      </div>
      <Row className="resources-column-wrapper" gutter={40}>
        <Col flex="1 1 658px" className="resources-column">
          <ResourceList>
            <ul className="resources-list">
              <li>
                <p
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      webFlowTutorialsVpd[0]?.['tutorial-intro'] ?? ''
                    )
                  }}
                />
              </li>
              <li>
                <div className="resources-item">
                  <div className="resources-item__title fw-bold width-inherit">
                    <ExploreLink
                      url={webFlowTutorialsVpd[0]?.['tour-item-link'] ?? ''}
                      title={webFlowTutorialsVpd[0]?.['tour-title'] ?? ''}
                    />
                  </div>
                  <span className="resources-item__subtitle text-chalkboard-txt">
                    {webFlowTutorialsVpd[0]?.['tour-label'] ?? ''}
                  </span>
                </div>
              </li>
              <li>
                <div className="resources-item">
                  <div className="resources-item__title fw-bold">
                    {webFlowTutorialsVpd[0]?.['getting-started-link'] !==
                    null ? (
                      <ExploreLink
                        url={
                          webFlowTutorialsVpd[0]?.['getting-started-link'] ?? ''
                        }
                        title={
                          webFlowTutorialsVpd[0]?.['getting-started-title'] ??
                          ''
                        }
                      />
                    ) : (
                      <Button
                        intent="icon"
                        className="getting-started-btn"
                        onClick={() => {
                          openVideo(
                            webFlowTutorialsVpd[0]?.['media-id'] ?? '',
                            webFlowTutorialsVpd[0]?.['friendly-name'] ?? ''
                          )
                        }}
                      >
                        {webFlowTutorialsVpd[0]['getting-started-title'] ?? ''}
                      </Button>
                    )}
                  </div>
                  <span className="resources-item__subtitle text-chalkboard-txt">
                    {webFlowTutorialsVpd[0]?.['getting-started-label'] ?? ''}
                  </span>
                </div>
              </li>
            </ul>
          </ResourceList>
        </Col>
        <Col flex="auto" className="resources-column">
          <iframe
            width="100%"
            height="194"
            src={webFlowTutorialsVpd[0]?.['video-link']}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </Col>
      </Row>
      {/* Tutorials */}
      {webFlowTutorials.map((tutorial) => {
        return (
          <Row
            gutter={[30, 16]}
            className="resources-column-wrapper"
            key={tutorial.name}
          >
            <Col span={24}>
              <h4 className="text-primary fw-bold suntex-resources-list__title">
                {tutorial.name}
                {tutorial['sub-title'] !== null &&
                tutorial['subtitle-link'] !== null ? (
                  <ExploreLink
                    url={tutorial['subtitle-link'] ?? ''}
                    title={tutorial['sub-title'] ?? ''}
                    className="text-primary fw-bold"
                  />
                ) : (
                  <Button
                    intent="icon"
                    className="getting-started-btn text-primary fw-bold"
                    onClick={() => {
                      openVideo(
                        tutorial['media-id'] ?? '',
                        tutorial['friendly-name'] ?? ''
                      )
                    }}
                  >
                    {tutorial['sub-title']}
                  </Button>
                )}
              </h4>
            </Col>
            {tutorial.tutorials?.map(
              (item: Entities_WebFlow_TutorialsLearningTools_TutorialsItem) => {
                return (
                  <Col xs={24} md={8} key={item.name}>
                    <Flex vertical className="card card--tutorial">
                      <div className="card-img-top">
                        <img
                          src={item['tutorials-icon'].url}
                          alt="Team activity chart"
                        />
                      </div>
                      <div className="card__body">
                        <div className="card-title fw-bold">{item.name}</div>
                        <div
                          className="card-text"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              item['tutorials-details'] ?? ''
                            )
                          }}
                        />
                      </div>
                      <div className="card__footer">
                        <Button
                          intent="secondary"
                          onClick={() => {
                            if (item['view-details-link'] !== null) {
                              window.open(
                                `${item['view-details-link'] ?? ''}`,
                                '_blank',
                                'noopener,noreferrer'
                              )
                            } else {
                              openVideo(item['media-id'], item['friendly-name'])
                            }
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </Flex>
                  </Col>
                )
              }
            )}
          </Row>
        )
      })}
      {/* Focus games */}
      {webFlowTutorialsGameExamples.map((gameExample) => {
        return (
          <Row
            className="resources-column-wrapper"
            gutter={[30, 20]}
            key={gameExample.name}
          >
            <Col span={24}>
              <h4 className="text-primary fw-bold suntex-resources-list__title">
                {gameExample.name}
                <span>
                  <ExploreLink
                    url={gameExample['link-for-list-of-games'] ?? ''}
                    title={gameExample['list-of-game-lable'] ?? ''}
                  />
                </span>
              </h4>
            </Col>
            {/* Game examples */}
            {gameExample.subCategories
              ?.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0)
              .map((subCategory) => (
                <GameExampleSubCategory
                  key={subCategory.name}
                  subCategory={subCategory}
                />
              ))}
          </Row>
        )
      })}
      {/* Additional links */}
      <Row className="resources-column-wrapper" gutter={[30, 20]}>
        <Col span={24}>
          <h4 className="text-primary fw-bold suntex-resources-list__title addtional-links-title">
            Additional Links
          </h4>
          <Row gutter={[10, 10]}>
            {Object.values(additionalLinks).map(
              (item: Entities_WebFlow_TutorialsAdditionalLinks_LinkItems) => (
                <Col xs={24} md={8} key={item.name}>
                  <div className="resources-item">
                    <div className="resources-item__title fw-bold">
                      {item.link !== null ? (
                        <ExploreLink
                          url={item.link ?? ''}
                          title={item.name ?? ''}
                        />
                      ) : (
                        <Button
                          intent="icon"
                          className="getting-started-btn"
                          onClick={() =>
                            openVideo(item['media-id'], item['friendly-name'])
                          }
                        >
                          {item.name ?? ''}
                        </Button>
                      )}
                    </div>
                  </div>
                </Col>
              )
            )}
          </Row>
        </Col>
      </Row>
    </Flex>
  )
}

export default TeacherTutorials
